import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Calendar, Clock, User, PenLine, Trash2, Eye } from 'lucide-react'
import { deletePost, fetchPostForEdit, updatePost } from '@/api/posts'
import AlertBanner from '@/components/AlertBanner'
import BackLink from '@/components/BackLink'
import { useAuthStore } from '@/stores/authStore'
import { HTTPError } from '@/api/client'
import { parseErrorDetail } from '@/api/parseError'
import LabelChip from '@/components/labels/LabelChip'
import CrossPostSection from '@/components/crosspost/CrossPostSection'
import ShareButton from '@/components/share/ShareButton'
import ShareBar from '@/components/share/ShareBar'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import RenderedContent from '@/components/RenderedContent'
import TableOfContents from '@/components/posts/TableOfContents'
import { usePost, useViewCount } from '@/hooks/usePost'
import { formatLocalDate } from '@/utils/date'
import { filePathToSlug } from '@/utils/postUrl'
import { readingTime } from '@/utils/readingTime'

export default function PostPage() {
  const { '*': slug } = useParams()
  const navigate = useNavigate()
  const { data: post, error: postError, isLoading: loading, mutate: mutatePost } = usePost(slug ?? null)
  const postSlug = slug !== undefined ? filePathToSlug(slug) : null
  const { data: viewData } = useViewCount(postSlug)
  const viewCount = viewData?.views ?? null
  const loadError = postError !== undefined
    ? postError instanceof HTTPError && postError.response.status === 404
      ? 'Post not found'
      : postError instanceof HTTPError && postError.response.status === 401
        ? 'Session expired. Please log in again.'
        : 'Failed to load post. Please try again later.'
    : null
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const user = useAuthStore((s) => s.user)
  const contentRef = useRef<HTMLDivElement>(null)
  useDocumentTitle(post?.title)

  async function handleDelete() {
    if (!post) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deletePost(post.file_path, true)
      void navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 401) {
        setDeleteError('Session expired. Please log in again.')
      } else {
        setDeleteError('Failed to delete post. Please try again.')
      }
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  async function handlePublish() {
    if (!post) return
    setPublishing(true)
    setPublishError(null)
    try {
      const editData = await fetchPostForEdit(post.file_path)
      const updated = await updatePost(post.file_path, {
        title: editData.title,
        subtitle: editData.subtitle,
        body: editData.body,
        labels: editData.labels,
        is_draft: false,
      })
      await mutatePost(updated, { revalidate: false })
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 401) {
        setPublishError('Session expired. Please log in again.')
      } else if (err instanceof HTTPError && err.response.status < 500) {
        const detail = await parseErrorDetail(
          err.response,
          'Failed to publish post. Please try again.',
        )
        setPublishError(detail)
      } else {
        setPublishError('Failed to publish post. Please try again.')
      }
    } finally {
      setPublishing(false)
    }
  }

  if (loading) {
    return (
      <div className="animate-fade-in" role="status" aria-label="Loading post">
        <div className="flex items-center justify-between mb-8">
          <div className="h-4 bg-border/40 rounded w-24 animate-pulse" />
        </div>
        <div className="mb-10 space-y-4">
          <div className="h-10 bg-border/50 rounded w-4/5 animate-pulse" />
          <div className="flex items-center gap-4">
            <div className="h-3.5 bg-border/30 rounded w-28 animate-pulse" />
            <div className="h-3.5 bg-border/30 rounded w-20 animate-pulse" />
          </div>
          <div className="h-px bg-border/40" />
        </div>
        <div className="space-y-4">
          <div className="h-4 bg-border/40 rounded w-full animate-pulse" />
          <div className="h-4 bg-border/40 rounded w-full animate-pulse" />
          <div className="h-4 bg-border/40 rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-border/40 rounded w-full animate-pulse" />
          <div className="h-4 bg-border/40 rounded w-5/6 animate-pulse" />
          <div className="h-4 bg-border/40 rounded w-2/3 animate-pulse" />
        </div>
      </div>
    )
  }

  if (loadError !== null || post == null) {
    return (
      <div className="text-center py-24">
        <p className="font-display text-3xl text-muted italic">
          {loadError === 'Post not found' ? '404' : 'Error'}
        </p>
        <p className="text-sm text-muted mt-2">{loadError ?? 'Post not found'}</p>
        <Link to="/" className="text-accent text-sm hover:underline mt-4 inline-block">
          Back to timeline
        </Link>
      </div>
    )
  }

  const dateStr = formatLocalDate(post.created_at, { dateStyle: 'long' })
  const canonicalPostSlug = filePathToSlug(post.file_path)
  const canonicalPostUrl = `${window.location.origin}/post/${canonicalPostSlug}`

  return (
    <article className="animate-fade-in xl:flex xl:gap-8">
      <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-8">
        <BackLink to="/" label="Back to posts" />
        <div className="xl:hidden">
          <TableOfContents contentRef={contentRef} />
        </div>
      </div>

      <header className="mb-10">
        <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight tracking-tight">
          {post.title}
        </h1>

        {post.subtitle != null && (
          <p data-testid="post-subtitle" className="font-display text-xl md:text-2xl text-ink/70 mt-2 leading-snug">
            {post.subtitle}
          </p>
        )}

        {user && post.is_draft && (
          <div className="mt-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg flex items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-800 dark:text-amber-300">
                Draft
              </div>
              <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                This post is not publicly visible yet
              </div>
            </div>
            <button
              onClick={() => void handlePublish()}
              disabled={publishing}
              className="px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent/90 rounded-lg transition-colors disabled:opacity-50 shrink-0"
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        )}

        {publishError !== null && (
          <AlertBanner variant="error" className="mt-3">{publishError}</AlertBanner>
        )}

        <div className="mt-5 text-sm text-muted">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Calendar size={14} />
              <time>{dateStr}</time>
            </div>

            {post.author !== null && (
              <div className="flex items-center gap-1.5">
                <User size={14} />
                <span>{post.author}</span>
              </div>
            )}

            {post.word_count > 0 && (
              <div className="flex items-center gap-1.5">
                <Clock size={14} aria-hidden="true" />
                <span>{readingTime(post.word_count)}</span>
              </div>
            )}

            {viewCount !== null && (
              <div className="flex items-center gap-1.5">
                <Eye size={14} aria-hidden="true" />
                <span>{viewCount.toLocaleString()} views</span>
              </div>
            )}

            {post.labels.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {post.labels.map((label) => (
                  <LabelChip key={label} labelId={label} />
                ))}
              </div>
            )}

            <ShareButton
              title={post.title}
              author={post.author}
              url={canonicalPostUrl}
              disabled={post.is_draft}
            />
          </div>

          {user && (
            <div className="flex items-center gap-3 mt-3">
              <Link
                to={`/editor/${post.file_path}`}
                className="flex items-center gap-1 text-accent hover:underline"
              >
                <PenLine size={14} />
                Edit
              </Link>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="flex items-center gap-1 text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 h-px bg-gradient-to-r from-accent/40 via-border to-transparent" />
      </header>

      {deleteError !== null && (
        <AlertBanner variant="error" className="mb-6">{deleteError}</AlertBanner>
      )}

      <RenderedContent html={post.rendered_html} contentRef={contentRef} />

      <ShareBar
        title={post.title}
        author={post.author}
        url={`${window.location.origin}/post/${slug}`}
        disabled={post.is_draft}
      />

      {!!user && post.file_path !== '' && (
        <CrossPostSection filePath={post.file_path} post={post} />
      )}

      <footer className="mt-16 pt-8 border-t border-border">
        <BackLink to="/" label="Back to posts" />
      </footer>
      </div>

      <aside className="hidden xl:block xl:w-56 xl:shrink-0">
        <TableOfContents contentRef={contentRef} variant="sidebar" />
      </aside>

      {showDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-paper border border-border rounded-xl shadow-xl p-6 max-w-sm mx-4 animate-fade-in">
              <h2 className="font-display text-xl text-ink mb-2">Delete post?</h2>
              <p className="text-sm text-muted mb-6">
                This will permanently delete &ldquo;{post.title}&rdquo;. This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-muted hover:text-ink
                           border border-border rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handleDelete()}
                  disabled={deleting}
                  data-testid="confirm-delete"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700
                           rounded-lg transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </article>
  )
}
